import React, { useState } from "react";
import { Paperclip } from "lucide-react";
import { formatLocalTimestamp } from "../lib/dateLocal";
import { resolveMediaUrl, fileIdFromRef } from "../lib/fileUrl";
import ImageUploader from "./ImageUploader";

/**
 * KAN-224 / KAN-232: public message thread for a support case.
 * `viewerRole` is "reporter" or "admin"; messages authored by the viewer are right-aligned.
 * `onSend(body, attachmentFileIds)` — attachmentFileIds optional (max 5).
 */
export default function SupportThread({
  caseId,
  messages = [],
  viewerRole,
  onSend,
  disabled = false,
  disabledReason = "",
  testidPrefix = "support-thread",
}) {
  const [body, setBody] = useState("");
  const [attachmentUrls, setAttachmentUrls] = useState([]);
  const [sending, setSending] = useState(false);
  const [showAttach, setShowAttach] = useState(false);
  const inputId = `${testidPrefix}-input-${caseId}`;

  const send = async () => {
    const text = body.trim();
    if (!text || sending) return;
    const attachment_file_ids = attachmentUrls
      .map((url) => fileIdFromRef(url))
      .filter(Boolean);
    setSending(true);
    try {
      const ok = await onSend(text, attachment_file_ids);
      if (ok !== false) {
        setBody("");
        setAttachmentUrls([]);
        setShowAttach(false);
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-3" data-testid={`${testidPrefix}-${caseId}`}>
      {messages.length > 0 && (
        <div className="space-y-2 max-h-64 overflow-y-auto mb-3" data-testid={`${testidPrefix}-messages-${caseId}`}>
          {messages.map((m) => {
            const mine = m.author_role === viewerRole;
            const files = m.attachment_file_ids || [];
            return (
              <div key={m.message_id} className={mine ? "flex justify-end" : "flex justify-start"}>
                <div
                  className={
                    "max-w-[85%] rounded-xl px-3 py-2 text-sm border "
                    + (mine
                      ? "bg-[var(--zc-accent-soft)] border-[var(--zc-border)]"
                      : "bg-[var(--zc-bg-3)] border-[var(--zc-border)]")
                  }
                >
                  <div className="text-xs text-[var(--zc-text-3)] mb-1">
                    {m.author_role === "admin" ? "Z-Cars support" : (m.author_name || "Reporter")}
                    {" · "}
                    {formatLocalTimestamp(m.created_at) || m.created_at}
                  </div>
                  <div className="text-[var(--zc-text)] whitespace-pre-wrap">{m.body}</div>
                  {files.length > 0 && (
                    <div
                      className="flex flex-wrap gap-1.5 mt-2"
                      data-testid={`${testidPrefix}-msg-attachments-${m.message_id}`}
                    >
                      {files.map((fid) => {
                        const href = resolveMediaUrl(`/api/files/${fid}`);
                        return (
                          <a
                            key={fid}
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-12 h-12 overflow-hidden rounded-md border border-[var(--zc-border)] hover:border-[var(--zc-accent)]"
                          >
                            <img src={href} alt="Attachment" className="w-full h-full object-cover" />
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {disabled ? (
        <div className="text-xs text-[var(--zc-text-3)]">{disabledReason}</div>
      ) : (
        <div>
          <label
            htmlFor={inputId}
            className="text-xs uppercase tracking-widest text-[var(--zc-text-3)] font-semibold"
          >
            {viewerRole === "admin" ? "Reply to reporter" : "Reply"}
          </label>
          <textarea
            id={inputId}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={4000}
            className="zc-input w-full p-3 text-sm mt-1 h-20"
            placeholder={viewerRole === "admin" ? "Visible to the reporter…" : "Write a message to our support team…"}
            data-testid={`${testidPrefix}-input-${caseId}`}
          />
          {showAttach && (
            <div className="mt-2" data-testid={`${testidPrefix}-attach-${caseId}`}>
              <ImageUploader
                value={attachmentUrls}
                onChange={setAttachmentUrls}
                max={5}
                testid={`${testidPrefix}-uploader-${caseId}`}
              />
            </div>
          )}
          <div className="flex items-center justify-between gap-2 mt-2">
            <button
              type="button"
              onClick={() => setShowAttach((v) => !v)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--zc-text-2)] hover:text-[var(--zc-accent)]"
              data-testid={`${testidPrefix}-attach-toggle-${caseId}`}
            >
              <Paperclip size={13} />
              {showAttach
                ? (attachmentUrls.length ? `Attachments (${attachmentUrls.length})` : "Hide attachments")
                : "Attach photos"}
            </button>
            <button
              type="button"
              onClick={send}
              disabled={sending || !body.trim()}
              className="px-4 h-9 zc-btn-primary text-xs disabled:opacity-50"
              data-testid={`${testidPrefix}-send-${caseId}`}
            >
              {sending ? "Sending…" : "Send reply"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
